import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { Chat } from '../types/chat';

interface ArchivedChatData {
  chat: {
    id: string;
    created_at: string;
    updated_at: string;
    vendor_id: string;
    vendor: {
      id: string;
      store_name: string;
      user_id: string;
      logo_image?: string;
    };
    chat_participants: {
      user_id: string;
    }[];
    messages: {
      id: string;
      text: string;
      created_at: string;
      sender_id: string;
    }[];
  };
  archived_at: string;
}

export function useArchivedChats() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['archived-chats', user?.id],
    queryFn: async () => {
      try {
        // First get the archived chat IDs and timestamps
        const { data: archivedChats, error: archiveError } = await supabase
          .from('archived_chats')
          .select('*')
          .eq('user_id', user?.id)
          .order('archived_at', { ascending: false });

        if (archiveError) throw archiveError;
        if (!archivedChats?.length) return [];

        // Then get the full chat data for these chats
        const { data: chatData, error: chatError } = await supabase
          .from('chats')
          .select(`
            *,
            chat_participants!inner (
              user_id
            ),
            vendor:vendors (
              id,
              store_name,
              user_id,
              logo_image
            ),
            messages (
              id,
              text,
              created_at,
              sender_id
            )
          `)
          .in('id', archivedChats.map(ac => ac.chat_id));

        if (chatError) throw chatError;
        if (!chatData?.length) return [];

        // Get all unique user IDs from participants and message senders
        const userIds = new Set<string>();
        chatData.forEach(chat => {
          chat.chat_participants.forEach(p => userIds.add(p.user_id));
          chat.messages?.forEach(m => userIds.add(m.sender_id));
        });

        // Fetch profiles for all participants and senders
        const { data: profiles } = await supabase
          .from('profiles')
          .select('*')
          .in('id', Array.from(userIds));

        const profilesMap = new Map(profiles?.map(p => [p.id, p]) || []);

        // Transform the data into the expected format
        return chatData.map(chat => {
          const archivedInfo = archivedChats.find(ac => ac.chat_id === chat.id);
          const messages = chat.messages || [];
          const lastMessage = messages.length > 0 
            ? messages.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
            : null;

          return {
            id: chat.id,
            created_at: chat.created_at,
            updated_at: chat.updated_at,
            vendor_id: chat.vendor_id,
            vendor: chat.vendor,
            participants: chat.chat_participants.map(participant => ({
              chat_id: chat.id,
              user_id: participant.user_id,
              user: profilesMap.get(participant.user_id)
            })),
            last_message: lastMessage ? {
              id: lastMessage.id,
              chat_id: chat.id,
              text: lastMessage.text,
              created_at: lastMessage.created_at,
              sender_id: lastMessage.sender_id,
              sender: profilesMap.get(lastMessage.sender_id)
            } : undefined,
            archived_at: archivedInfo?.archived_at
          };
        });
      } catch (error) {
        console.error('Error in archived chats query:', error);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
} 